import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import {
    TASK_ACTION_FORM_DEFAULTS,
    taskActionSchema,
    type TaskActionFormValues,
} from '@/entities/OrderTask/model/schema';
import { useOrderTasks } from '@/entities/OrderTask/model/store';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { usePayForTaskSubmit } from './usePayForTaskSubmit';
import classes from './PayForTaskModal.module.scss';

export interface PayForTaskModalProps {
    visible: boolean;
    onHide: () => void;
    loading?: boolean;
    orderId: string;
    taskId: number | null;
}

const { TextField: TaskCommentField } = createRHFFields<TaskActionFormValues>();

/** Отображает форму направления выплаты исполнителю по сделке. */
export const PayForTaskModal = (
    { taskId, visible, onHide, loading = false, orderId }: PayForTaskModalProps
) => {
    const getByOrderId = useOrderTasks((state) => state.getByOrderId);

    const form = useUnsavedForm<TaskActionFormValues>({
        resolver: zodResolver(taskActionSchema),
        defaultValues: TASK_ACTION_FORM_DEFAULTS,
    }, visible);

    const { onSubmit, completed, resetCompleted } = usePayForTaskSubmit(taskId, form);
    const { reset } = form;

    useEffect(() => {
        if (!visible) return;
        reset(TASK_ACTION_FORM_DEFAULTS);
        resetCompleted();
    }, [visible, taskId, reset, resetCompleted]);

    /** Закрывает оплату задачи с подтверждением изменений */
    const handleHide = () => confirmUnsavedChanges(form.formState.isDirty, () => {
        reset();

        if (completed && orderId) void getByOrderId(orderId);
        onHide();
    });

    return <Modal
        visible={visible}
        onHide={handleHide}
        title="Направить выплату исполнителю за заказ"
        size="md"
        closable={!form.formState.isSubmitting}
        footer={<div className={classes.footer}>
            <Button
                type="button"
                label="Отмена"
                text
                onClick={handleHide}
                disabled={form.formState.isSubmitting}
            />
            {!loading && !completed && taskId !== null &&
                <Button
                    type="submit"
                    form="pay-task-form"
                    label="Направить выплату"
                    loading={form.formState.isSubmitting}
                    disabled={form.formState.isSubmitting}
                />
            }
        </div>}
    >
        {loading &&
            <RequestLoading message="Загрузка сделки..." />
        }
        {!loading && taskId === null &&
            <RequestEmpty message="Номер задания не найден" />
        }
        {!loading && taskId !== null && completed &&
            <Message
                severity="success"
                text="Оплата за заказ была направлена"
            />
        }
        {!loading && taskId !== null && !completed &&
            <FormProvider {...form}>
                <form
                    id="pay-task-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    className={classes.form}
                    noValidate
                >
                    <TaskCommentField
                        name="comment"
                        label="Комментарий"
                        placeholder="Комментарий"
                    />
                    {form.formState.errors.root?.server?.message &&
                        <RequestError
                            message={form.formState.errors.root.server.message}
                            compact
                        />
                    }
                </form>
            </FormProvider>
        }
    </Modal>;
};
